import React, { useState } from 'react';
import { ltMonths, weekdaysLt } from '../functions/monthDaysConstants';
import { getDaysInMonth } from '../functions/date';
import Stats from './Stats';
import { getStats } from '../functions/getStats';

type MonthGridProps = {
    year: number;
    periods: { start: string; end: string }[];
    setPeriods: React.Dispatch<React.SetStateAction<{ start: string; end: string }[]>>;
    holidayDates: Map<string, string>;
    periodDays: Set<string>;
    getDateRange: (start: string, end: string) => Set<string>;
    getStartDay: (year: number, month: number) => number;
    enableMonthNavigation?: boolean;
    workWeek: 'five' | 'six';
};

const MonthGrid = ({ year, periodDays, periods, getDateRange, setPeriods, getStartDay, holidayDates, enableMonthNavigation, workWeek }: MonthGridProps) => {
    const [startDate, setStartDate] = useState<string | null>(null);
    const [hoverDate, setHoverDate] = useState<string | null>(null);

    const formatDateStr = (year: number, month: number, day: number) =>
        `${year}-${(month + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;

    const dynamicRange = startDate && hoverDate && !periodDays.has(hoverDate)
        ? getDateRange(startDate, hoverDate)
        : new Set<string>();

    const findPeriodIndex = (date: string) => periods.findIndex(({ start, end }) => getDateRange(start, end).has(date));

    const onDayClick = (dateStr: string) => {
        const index = findPeriodIndex(dateStr);
        if (index !== -1) {
            const newPeriods = [...periods];
            newPeriods.splice(index, 1);
            setPeriods(newPeriods);
            setStartDate(null);
            setHoverDate(null);
            return;
        }

        if (!startDate) {
            setStartDate(dateStr);
            setHoverDate(null);
        } else {
            const newPeriod = {
                start: startDate < dateStr ? startDate : dateStr,
                end: startDate > dateStr ? startDate : dateStr
            };
            setPeriods([...periods, newPeriod]);
            setStartDate(null);
            setHoverDate(null);
        }
    };
    const onDayHover = (dateStr: string) => startDate && setHoverDate(dateStr);

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 max-w-4xl mx-auto mt-6">
            {ltMonths.map((month, monthIndex) => {
                const daysInMonth = getDaysInMonth(year, monthIndex);
                const startDay = getStartDay(year, monthIndex);
                const days: (number | null)[] = [];
                for (let i = 0; i < startDay; i++) days.push(null);
                for (let d = 1; d <= daysInMonth; d++) days.push(d);

                const stats5 = getStats(year, monthIndex, false);
                const stats6 = getStats(year, monthIndex, true);

                return (
                    <div key={month} className="border rounded-xl shadow-sm bg-white p-3">
                        <h2 className="text-center font-semibold mb-2 capitalize text-lg text-blue-700">
                            {enableMonthNavigation ? (
                                <a href={`/darbo-laiko-kalendorius/${year}/${monthIndex + 1}/`} className="hover:underline">
                                    {month}
                                </a>
                            ) : (
                                month
                            )}
                        </h2>
                        <div className="grid grid-cols-7 text-xs text-center mb-1 text-gray-600">
                            {weekdaysLt.map((d, idx) => <div key={d} className={idx >= 5 ? 'bg-blue-100 rounded' : ''}>{d}</div>)}
                        </div>
                        <div className="grid grid-cols-7 gap-1 text-center text-sm">
                            {days.map((day, i) => {
                                if (!day) return <div key={i}></div>;
                                const dateStr = formatDateStr(year, monthIndex, day);
                                const isInPeriod = periodDays.has(dateStr);
                                const isInRange = dynamicRange.has(dateStr);
                                const isHoliday = holidayDates.has(dateStr);
                                const holidayName = holidayDates.get(dateStr);
                                const dayOfWeek = new Date(dateStr).getDay();

                                let classNames = 'aspect-square flex items-center justify-center rounded cursor-pointer transition ';
                                if (i % 7 >= 5) classNames += 'bg-blue-50 ';
                                if (isHoliday && !isInPeriod) classNames += 'bg-pink-300 text-pink-900 font-semibold ';
                                else if (isHoliday && isInPeriod) classNames += 'bg-green-200 text-green-900 font-semibold ';
                                else if (isInPeriod) classNames += (dayOfWeek === 0 || dayOfWeek === 6) ? 'bg-green-700 text-white font-bold ' : 'bg-green-400 text-white font-bold ';
                                else if (isInRange) classNames += 'bg-yellow-300 font-semibold ';
                                else classNames += 'hover:bg-gray-100 ';

                                return <div key={i} title={holidayName ? `${dateStr} – ${holidayName}` : dateStr} onClick={() => onDayClick(dateStr)} onMouseEnter={() => onDayHover(dateStr)} className={classNames}>{day}</div>
                            })}
                        </div>
                        <Stats stats5={stats5} stats6={stats6} workWeek={workWeek} />
                    </div>
                );
            })}
        </div>
    );
};

export default MonthGrid;
